/**
 * BuilderToolbar Component
 * 
 * Top toolbar with save, publish, undo/redo, and view mode controls.
 */

import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useMenuBuilderStore, useUndoRedo, useIsDirty } from '@/stores/menuBuilderStore';
import { ViewMode } from '@/types/menuBuilder';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Undo2,
  Redo2,
  Smartphone,
  Tablet,
  Save,
  Upload,
  Eye,
  ArrowLeft,
  Loader2,
  Store,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface Branch {
  id: string;
  name: string;
}

interface BuilderToolbarProps {
  className?: string;
  onSave: () => void;
  onPublish: () => void;
  onPreview: () => void;
  isSaving?: boolean;
  isPublishing?: boolean;
  isPublished?: boolean;
  branches?: Branch[];
  selectedBranchId?: string | null;
  onBranchChange?: (branchId: string) => void;
}

export const BuilderToolbar = memo(function BuilderToolbar({
  className,
  onSave,
  onPublish,
  onPreview,
  isSaving = false,
  isPublishing = false,
  isPublished = false,
  branches = [],
  selectedBranchId,
  onBranchChange,
}: BuilderToolbarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const viewMode = useMenuBuilderStore((s) => s.viewMode);
  const setViewMode = useMenuBuilderStore((s) => s.setViewMode);
  const { canUndo, canRedo, undo, redo } = useUndoRedo();
  const isDirty = useIsDirty();

  const handleBack = useCallback(() => {
    if (isDirty) {
      if (window.confirm(t('menuBuilder.unsavedChanges', 'You have unsaved changes. Are you sure you want to leave?'))) {
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  }, [isDirty, navigate, t]);

  const handleViewModeChange = useCallback(
    (mode: ViewMode) => {
      setViewMode(mode);
    },
    [setViewMode]
  );

  const handleBranchChange = (value: string) => {
    if (isDirty) {
      if (window.confirm(t('menuBuilder.unsavedChangesSwitchBranch', 'You have unsaved changes. Switching branches will discard them. Continue?'))) {
        onBranchChange?.(value);
      }
    } else {
      onBranchChange?.(value);
    }
  };

  return (
    <div
      className={cn(
        'h-14 bg-background border-b flex items-center justify-between px-4',
        className
      )}
    >
      {/* Left section - Back button, title, and branch selector */}
      <div className="flex items-center gap-4">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" onClick={handleBack}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('common.back', 'Back')}</TooltipContent>
        </Tooltip>

        <div className="flex items-center gap-4">
          <h1 className="font-semibold hidden md:block">
            {t('menuBuilder.title', 'Menu Builder')}
          </h1>
          
          {branches.length > 0 && (
            <div className="flex items-center gap-2">
              <Separator orientation="vertical" className="h-6 hidden md:block" />
              <Select
                value={selectedBranchId || ''}
                onValueChange={handleBranchChange}
              >
                <SelectTrigger className="w-[180px] h-8 text-xs">
                  <div className="flex items-center gap-2 truncate">
                    <Store className="h-3 w-3 text-muted-foreground" />
                    <SelectValue placeholder={t('common.selectBranch', 'Select Branch')} />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {branches.map((branch) => (
                    <SelectItem key={branch.id} value={branch.id} className="text-xs">
                      {branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {isDirty && (
          <Badge variant="secondary" className="text-xs">
            {t('menuBuilder.unsaved', 'Unsaved')}
          </Badge>
        )}

        {isPublished && !isDirty && (
          <Badge variant="default" className="text-xs bg-green-600">
            {t('menuBuilder.published', 'Published')}
          </Badge>
        )}
      </div>

      {/* Center section - View mode and undo/redo */}
      <div className="flex items-center gap-2">
        {/* Undo/Redo */}
        <div className="flex items-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={undo}
                disabled={!canUndo}
              >
                <Undo2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('common.undo', 'Undo')}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={redo}
                disabled={!canRedo}
              >
                <Redo2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('common.redo', 'Redo')}</TooltipContent>
          </Tooltip>
        </div>

        <Separator orientation="vertical" className="h-6" />

        {/* View Mode */}
        <div className="flex items-center bg-muted rounded-lg p-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={viewMode === 'mobile' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-7 px-2"
                onClick={() => handleViewModeChange('mobile')}
              >
                <Smartphone className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('menuBuilder.mobile', 'Mobile')}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={viewMode === 'desktop' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-7 px-2"
                onClick={() => handleViewModeChange('desktop')}
              >
                <Tablet className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('menuBuilder.tablet', 'Tablet')}</TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Right section - Actions */}
      <div className="flex items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="outline" size="sm" onClick={onPreview}>
              <Eye className="h-4 w-4 mr-2" />
              {t('menuBuilder.preview', 'Preview')}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {t('menuBuilder.previewHint', 'Preview how customers will see the menu')}
          </TooltipContent>
        </Tooltip>

        <Button
          variant="outline"
          size="sm"
          onClick={onSave}
          disabled={isSaving || !isDirty}
        >
          {isSaving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          {t('common.save', 'Save')}
        </Button>

        <Button
          variant="default"
          size="sm"
          onClick={onPublish}
          disabled={isPublishing || isDirty}
        >
          {isPublishing ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Upload className="h-4 w-4 mr-2" />
          )}
          {t('menuBuilder.publish', 'Publish')}
        </Button>
      </div>
    </div>
  );
});

export default BuilderToolbar;
